import { zodResolver } from '@hookform/resolvers/zod';
import {
  type DueDiligenceFrequency,
  type SettingsFormData,
  SettingsFormSchema,
} from '@nestjs-boilerplate/types';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { TermsConditionsModal } from '@/components/terms-conditions-modal';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { DUE_DILIGENCE_FREQUENCY_OPTIONS, TITLE_GROUPS } from '@/constants/industries';
import {
  type PricingItem,
  type ProfileResponse,
  useChangePasswordRequest,
  usePricing,
  useProfile,
  useUpdateCompany,
  useUpdateDueDiligence,
  useUpdateProfile,
} from '../api/settings';
import { AddFundsModal } from './add-funds-modal';
import { DeleteAccountModal } from './delete-account-modal';
import { SettingsAddressSection } from './settings-address-section';
import { SettingsBillingSection } from './settings-billing-section';
import { SettingsCompanySection } from './settings-company-section';
import { SettingsPersonalAddressSection } from './settings-personal-address-section';

// Inner form component that only mounts when profile is available
function SettingsForm({
  profile,
  pricing,
}: {
  profile: ProfileResponse;
  pricing: PricingItem[] | undefined;
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const { mutateAsync: updateProfile, isPending: isUpdatingProfile } = useUpdateProfile();
  const { mutateAsync: updateCompany, isPending: isUpdatingCompany } = useUpdateCompany();
  const { mutateAsync: updateDueDiligence, isPending: isUpdatingDueDiligence } =
    useUpdateDueDiligence();
  const { mutate: changePassword, isPending: isChangingPassword } = useChangePasswordRequest();

  // Initialize form with profile data - this only runs once when component mounts
  const form = useForm<SettingsFormData>({
    resolver: zodResolver(SettingsFormSchema),
    defaultValues: {
      title: profile.user.title?.toUpperCase() || '',
      forename: profile.user.forename || '',
      middleName: profile.user.middleName || '',
      surname: profile.user.surname || '',
      jobTitle: profile.user.jobTitle || '',
      personalAddressLine1: profile.user.addressLine1 || '',
      personalAddressLine2: profile.user.addressLine2 || '',
      personalTownCity: profile.user.townCity || '',
      personalCounty: profile.user.county || '',
      personalPostcode: profile.user.postcode || '',
      personalCountry: profile.user.country || 'GB',
      dueDiligenceAlertFrequency:
        (profile.user.dueDiligenceAlertFrequency as DueDiligenceFrequency) || 'NEVER',
      companyName: profile.company?.name || '',
      industry: profile.company?.industry || 'SOLICITORS',
      companyNumber: profile.company?.companyNumber || '',
      companyWebsite: profile.company?.website || '',
      addressLine1: profile.company?.addressLine1 || '',
      addressLine2: profile.company?.addressLine2 || '',
      townCity: profile.company?.townCity || '',
      county: profile.company?.county || '',
      postcode: profile.company?.postcode || '',
      country: profile.company?.country || 'GB',
    },
  });

  const isSaving = isUpdatingProfile || isUpdatingCompany || isUpdatingDueDiligence;

  async function handleSave(data: SettingsFormData) {
    // Empty strings for new fields (jobTitle, personalAddress*, companyNumber)
    // mean 'leave as-is' for existing users who haven't filled them in yet.
    // Map '' → undefined so the API treats them as omitted rather than
    // overwriting with empty strings (or rejecting the 8-char regex).
    const blankToUndefined = (value: string | undefined): string | undefined =>
      value && value.length > 0 ? value : undefined;

    try {
      await Promise.all([
        // Update profile (user info + personal address). The form uses
        // `personal*` prefixed fields to disambiguate visually from the
        // company registered address; the UpdateProfileDto drops the
        // prefix because its scope is already 'the user'.
        updateProfile({
          title: data.title,
          forename: data.forename,
          middleName: data.middleName || null,
          surname: data.surname,
          jobTitle: blankToUndefined(data.jobTitle),
          addressLine1: blankToUndefined(data.personalAddressLine1),
          addressLine2: data.personalAddressLine2 || null,
          townCity: blankToUndefined(data.personalTownCity),
          county: data.personalCounty || null,
          postcode: blankToUndefined(data.personalPostcode),
          country: blankToUndefined(data.personalCountry),
        }),
        // Update due diligence
        updateDueDiligence({
          frequency: data.dueDiligenceAlertFrequency,
        }),
        // Update company (registered Companies House address)
        updateCompany({
          name: data.companyName,
          industry: data.industry,
          website: data.companyWebsite || null,
          companyNumber: blankToUndefined(data.companyNumber),
          addressLine1: data.addressLine1,
          addressLine2: data.addressLine2 || null,
          townCity: data.townCity,
          county: data.county || null,
          postcode: data.postcode,
          country: data.country,
        }),
      ]);

      toast.success('Profile updated successfully');
    } catch {
      // Individual errors are handled by the mutation hooks
    }
  }

  function handleChangePassword() {
    changePassword();
  }

  function handleLogout() {
    window.location.href = '/logout';
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        {/* Header - title aligned with content, Save button at far right */}
        <div className="relative mb-8 pt-8">
          <div className="mx-auto max-w-4xl px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <h1 className="text-2xl font-semibold text-[#051923]">Account Settings</h1>
              <Button
                type="submit"
                disabled={isSaving}
                className="h-10 w-[140px] bg-[#131313] text-white hover:bg-[#131313]/90 sm:absolute sm:right-6 sm:top-8"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>

        {/* Constrained Content */}
        <div className="mx-auto max-w-4xl px-6 pb-8">
          {/* Email Address Section */}
          <section className="mb-8">
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-700">Email Address</span>
                <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-600">
                  {profile.user.email}
                </div>
              </div>
              <button
                type="button"
                onClick={handleChangePassword}
                disabled={isChangingPassword}
                className="text-sm font-semibold text-[#131313] underline hover:text-[#131313]/80"
              >
                {isChangingPassword ? 'Processing...' : 'Change Password'}
              </button>
            </div>
          </section>

          <Separator className="my-8" />

          {/* User Information Section */}
          <section className="mb-8">
            <h2 className="mb-6 text-lg font-semibold text-[#051923]">User Information</h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TITLE_GROUPS.map((group, groupIndex) => (
                          <SelectGroup key={group.category}>
                            {groupIndex > 0 && <div className="my-1 border-t border-gray-200" />}
                            <SelectLabel className="text-xs text-gray-500">
                              {group.category}
                            </SelectLabel>
                            {group.titles.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="forename"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Forename</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="middleName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Middle Name(s)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="surname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Surname</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="jobTitle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Title</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Compliance Officer" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </section>

          <Separator className="my-8" />

          <SettingsPersonalAddressSection form={form} />

          <Separator className="my-8" />

          {/* Due Diligence Alert Section */}
          <section className="mb-8">
            <h2 className="mb-2 text-lg font-semibold text-[#051923]">
              Customer Due Diligence Alert
            </h2>
            <p className="mb-4 text-sm text-gray-600">
              Please select the frequency you would like to be notified that certain clients need
              their risk assessments refreshing.
            </p>
            <FormField
              control={form.control}
              name="dueDiligenceAlertFrequency"
              render={({ field }) => (
                <FormItem className="max-w-[200px]">
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {DUE_DILIGENCE_FREQUENCY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>

          <Separator className="my-8" />

          <SettingsCompanySection form={form} />

          <Separator className="my-8" />

          <SettingsAddressSection form={form} />

          <Separator className="my-8" />

          <SettingsBillingSection
            pricing={pricing}
            balanceFormatted={
              profile.user.balance ? `£${parseFloat(profile.user.balance).toFixed(2)}` : '£0.00'
            }
            onAddFunds={() => setShowAddFundsModal(true)}
          />

          <Separator className="my-8" />

          {/* Footer Links */}
          <section className="space-y-4">
            <button
              type="button"
              onClick={() => setShowTermsModal(true)}
              className="block text-sm font-semibold text-[#131313] underline hover:text-[#131313]/80"
            >
              Terms &amp; Conditions
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="block text-sm font-semibold text-[#131313] underline hover:text-[#131313]/80"
            >
              Log out
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="block text-sm font-semibold text-red-600 underline hover:text-red-700"
            >
              Delete Account
            </button>
          </section>
        </div>
      </form>

      {/* Modals */}
      <DeleteAccountModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        userEmail={profile.user.email}
      />
      <AddFundsModal open={showAddFundsModal} onOpenChange={setShowAddFundsModal} />
      <TermsConditionsModal open={showTermsModal} onOpenChange={setShowTermsModal} mode="view" />
    </Form>
  );
}

// Wrapper component that handles loading state
export function SettingsPage() {
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: pricing } = usePricing();

  if (profileLoading || !profile) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Key forces remount when user ID changes, ensuring fresh form state
  return <SettingsForm key={profile.user.id} profile={profile} pricing={pricing} />;
}
